import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap'
import { FaArrowRotateRight, FaArrowsRotate, FaChevronDown, FaChevronRight, FaDownload, FaMagnifyingGlass, FaPlus } from 'react-icons/fa6'
import { FaCheckCircle, FaDatabase, FaEdit, FaExclamationTriangle, FaFile, FaLink, FaRss } from 'react-icons/fa'
import * as DateFns from 'date-fns'
import FeedInputModal from './feed-input-modal'
import { IFeed, IFeed_Input, IFeed_Input_UpdateInterval } from './types'
import { formatNumberLocale, getUserTimezoneOffset } from '../../../_utils'
import useFetch from '../../../_fetch'
import DownloadModal from './download-modal'
import { useAuth } from '../../../auth/auth-context'
import ConfirmRemoveModal from './confirm-remove-modal'
import './feed.css'

const isDev = process.env.NODE_ENV === 'development'

const formatUpdateInterval = (interval: IFeed_Input_UpdateInterval): string => {
    if (interval.frequency === 'hourly' || !('timezoneOffset' in interval)) return 'Every hour'

    const { frequency, hour, dayOfWeek, dayOfMonth } = interval
    const formattedTime = `${hour || '00'}:00`

    switch (frequency) {
        case 'daily':
            return `Daily · ${formattedTime}`
        case 'weekly':
            return `${dayOfWeek || 'Sunday'} · ${formattedTime}`
        case 'monthly':
            return `Day ${dayOfMonth || '1'} · ${formattedTime}`
        default:
            return 'Schedule unavailable'
    }
}

const formatGMT = (interval: IFeed_Input_UpdateInterval): string => {
    if (interval.frequency === 'hourly' || !('timezoneOffset' in interval)) return ''
    const { timezoneOffset } = interval
    return `GMT${timezoneOffset >= 0 ? '+' : ''}${timezoneOffset}`
}

const formatLastUpdated = (lastUpdated?: string): string => {
    if (!lastUpdated) return 'Never'
    const date = new Date(lastUpdated)
    return DateFns.isValid(date) ? DateFns.format(date, 'MMM d, yyyy, HH:mm') : 'Unavailable'
}

const getStatus = (output: IFeed['output'][number] | undefined, isUpdating: boolean) => {
    if (isUpdating) return { label: 'Updating', variant: 'primary' }
    if (!output) return { label: 'Pending', variant: 'secondary' }
    if (output.status === 1) return { label: 'Successful', variant: 'success' }
    if (output.status === 2) return { label: 'Warning', variant: 'warning' }
    return { label: 'Error', variant: 'danger' }
}

type FeedOverviewStatus = 'ready' | 'updating' | 'warning' | 'error' | 'pending'
type FeedFilter = 'all' | 'attention' | 'updating' | 'ready'
type FeedSort = 'attention' | 'recent' | 'name'

const getFeedOverview = (feed: IFeed, runningFeeds: Record<string, boolean>) => {
    const isUpdating = feed.input.links.some((link) => runningFeeds[`${feed.id}_${link.id}`])
    const hasError = feed.output.some((output) => output.status !== 1 && output.status !== 2)
    const hasWarning = feed.output.some((output) => output.status === 2)
    const isPending = feed.input.links.length === 0 || feed.input.links.some((link) => (
        !feed.output.some((output) => output.linkId === link.id)
    ))
    const status: FeedOverviewStatus = isUpdating
        ? 'updating'
        : hasError
            ? 'error'
            : hasWarning
                ? 'warning'
                : isPending
                    ? 'pending'
                    : 'ready'
    const statusConfig = {
        ready: { label: 'Successful', variant: 'success' },
        updating: { label: 'Updating', variant: 'primary' },
        warning: { label: 'Warning', variant: 'warning' },
        error: { label: 'Error', variant: 'danger' },
        pending: { label: 'Pending', variant: 'secondary' },
    }[status]
    const latestTimestamp = feed.output.reduce((latest, output) => {
        if (!output.lastUpdated) return latest
        const timestamp = new Date(output.lastUpdated).getTime()
        return Number.isFinite(timestamp) ? Math.max(latest, timestamp) : latest
    }, 0)

    return { status, ...statusConfig, latestTimestamp }
}

const getEmptyFeed = (): IFeed => ({
    id: '',
    input: {
        name: '',
        rules: [],
        updateInterval: {
            frequency: 'daily',
            hour: '00',
            timezoneOffset: getUserTimezoneOffset(),
        },
        links: [],
    },
    output: [],
})

const Feed = () => {
    const { doFetch } = useFetch()
    const { user } = useAuth()

    const [showModal, setShowModal] = useState(false)
    const [feeds, setFeeds] = useState<IFeed[]>([])
    const [runningFeeds, setRunningFeeds] = useState<Record<string, boolean>>({})
    const [currentFeed, setCurrentFeed] = useState<IFeed | undefined>(undefined)
    const [isLoading, setIsLoading] = useState(true)
    const [pageError, setPageError] = useState('')
    const [notice, setNotice] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [feedFilter, setFeedFilter] = useState<FeedFilter>('all')
    const [feedSort, setFeedSort] = useState<FeedSort>('attention')
    const [expandedFeedIds, setExpandedFeedIds] = useState<Set<string>>(() => new Set())
    const [currentPage, setCurrentPage] = useState(1)

    const [downloadModalVisible, setDownloadModalVisible] = useState(false)
    const [downloadInfo, setDownloadInfo] = useState({ title: '', link: '' })

    const [showConfirmRemoveModal, setShowConfirmRemoveModal] = useState(false)
    const [feedToRemove, setFeedToRemove] = useState<IFeed | null>(null)
    const [isRemoving, setIsRemoving] = useState(false)
    const [removeError, setRemoveError] = useState('')

    const isRefreshing = useRef(false)
    const isMounted = useRef(true)

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    const refreshFeeds = async (currFeeds: Record<string, boolean>): Promise<void> => {
        if (isRefreshing.current || Object.keys(currFeeds).length === 0) return

        isRefreshing.current = true
        await new Promise((resolve) => setTimeout(resolve, 5000))

        if (!isMounted.current) {
            isRefreshing.current = false
            return
        }

        try {
            const runningResponse = await doFetch('/feed/get-running-feeds')
            if (runningResponse.status >= 400) throw new Error('Could not refresh running feeds.')

            const newRunningFeeds = await runningResponse.json()
            if (!isMounted.current) return
            setRunningFeeds(newRunningFeeds)

            if (Object.keys(newRunningFeeds).length === 0) {
                const feedResponse = await doFetch('/feed/get-feeds')
                if (feedResponse.status >= 400) throw new Error('Could not refresh feed results.')

                const data = await feedResponse.json()
                if (!isMounted.current) return
                setFeeds(Array.isArray(data.feeds) ? data.feeds : [])
                setRunningFeeds(data.runningFeeds || {})
                isRefreshing.current = false
                return refreshFeeds(data.runningFeeds || {})
            }

            isRefreshing.current = false
            return refreshFeeds(newRunningFeeds)
        } catch (error) {
            if (isMounted.current) {
                setPageError(error instanceof Error ? error.message : 'Could not refresh feed results.')
            }
            isRefreshing.current = false
        }
    }

    const loadFeeds = async () => {
        setIsLoading(true)
        setPageError('')
        try {
            const response = await doFetch('/feed/get-feeds')
            if (response.status >= 400) throw new Error('Feeds could not be loaded. Please try again.')

            const data = await response.json()
            if (!isMounted.current) return
            setFeeds(Array.isArray(data.feeds) ? data.feeds : [])
            setRunningFeeds(data.runningFeeds || {})
            refreshFeeds(data.runningFeeds || {})
        } catch (error) {
            if (isMounted.current) {
                setPageError(error instanceof Error ? error.message : 'Feeds could not be loaded. Please try again.')
            }
        } finally {
            if (isMounted.current) setIsLoading(false)
        }
    }

    useEffect(() => {
        loadFeeds()
        // The authenticated fetch callback is stable for the lifetime of this page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleOpenModal = (feed: IFeed) => {
        setCurrentFeed(feed)
        setShowModal(true)
        setNotice('')
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setCurrentFeed(undefined)
    }

    const handleSaveFeedInput = async (input: IFeed_Input) => {
        const isEditing = Boolean(currentFeed?.id)
        const url = isEditing ? '/feed/update-feed' : '/feed/create-feed'

        const formData = new FormData()
        const fileLinkIds: string[] = []
        const sanitizedLinks = input.links.map((link) => {
            if (typeof link.file !== 'string' && link.file) {
                formData.append('files', link.file)
                fileLinkIds.push(link.id)
                return { ...link, file: link.file.name }
            }
            return { ...link }
        })
        const sanitizedInput = { ...input, links: sanitizedLinks }

        formData.append('id', currentFeed?.id || '')
        formData.append('input', JSON.stringify(sanitizedInput))
        formData.append('fileLinkIds', JSON.stringify(fileLinkIds))

        const response = await doFetch(url, formData)
        if (response.status >= 400) throw new Error('The feed could not be saved. Check the fields and try again.')

        const data = await response.json()
        setFeeds((currentFeeds) => {
            if (!isEditing) return [...currentFeeds, { ...data, output: data.output || [] }]
            return currentFeeds.map((feed) => (
                feed.id === currentFeed?.id ? { ...feed, input: data.input } : feed
            ))
        })
        setNotice(isEditing ? 'Feed changes saved.' : 'Feed created successfully.')
    }

    const updateFeed = async (feedId: string, linkId: string) => {
        const runningKey = `${feedId}_${linkId}`
        setPageError('')
        setNotice('')
        setRunningFeeds((current) => ({ ...current, [runningKey]: true }))

        try {
            const response = await doFetch('/feed/run-feed', { feedId, linkId })
            if (response.status >= 400) throw new Error('This source could not be updated. Please try again.')

            const currRunningFeeds = await response.json()
            setRunningFeeds(currRunningFeeds)
            refreshFeeds(currRunningFeeds)
        } catch (error) {
            setRunningFeeds((current) => {
                const next = { ...current }
                delete next[runningKey]
                return next
            })
            setPageError(error instanceof Error ? error.message : 'This source could not be updated. Please try again.')
        }
    }

    const handleDownloadClick = (link: string, title: string) => {
        setDownloadInfo({ title, link })
        setDownloadModalVisible(true)
    }

    const handleDownloadModalClose = () => {
        setDownloadModalVisible(false)
        setDownloadInfo({ title: '', link: '' })
    }

    const handleRemoveFeed = async () => {
        if (!feedToRemove) return
        setIsRemoving(true)
        setRemoveError('')
        try {
            const response = await doFetch('/feed/remove-feed/', { feedId: feedToRemove.id })
            if (response.status >= 400) throw new Error('The feed could not be removed. Please try again.')

            setFeeds((currentFeeds) => currentFeeds.filter((feed) => feed.id !== feedToRemove.id))
            setNotice(`“${feedToRemove.input.name}” was removed.`)
            setShowConfirmRemoveModal(false)
            setFeedToRemove(null)
            handleCloseModal()
        } catch (error) {
            setRemoveError(error instanceof Error ? error.message : 'The feed could not be removed. Please try again.')
        } finally {
            setIsRemoving(false)
        }
    }

    const sourceCount = useMemo(
        () => feeds.reduce((total, feed) => total + feed.input.links.length, 0),
        [feeds]
    )
    const attentionCount = useMemo(
        () => feeds.reduce(
            (total, feed) => total + feed.output.filter((output) => output.status === 2 || output.status === 3).length,
            0
        ),
        [feeds]
    )
    const updatingCount = Object.values(runningFeeds).filter(Boolean).length
    const feedRows = useMemo(() => feeds.map((feed) => ({
        feed,
        overview: getFeedOverview(feed, runningFeeds),
    })), [feeds, runningFeeds])
    const filteredFeedRows = useMemo(() => {
        const normalizedSearch = searchQuery.trim().toLowerCase()
        const statusRank: Record<FeedOverviewStatus, number> = { error: 0, warning: 1, updating: 2, pending: 3, ready: 4 }
        return feedRows
            .filter(({ feed, overview }) => {
                const matchesSearch = !normalizedSearch
                    || feed.input.name.toLowerCase().includes(normalizedSearch)
                    || feed.input.links.some((link) => (link.name || '').toLowerCase().includes(normalizedSearch))
                const matchesFilter = feedFilter === 'all'
                    || (feedFilter === 'attention' && (overview.status === 'error' || overview.status === 'warning'))
                    || overview.status === feedFilter
                return matchesSearch && matchesFilter
            })
            .sort((first, second) => {
                if (feedSort === 'name') return first.feed.input.name.localeCompare(second.feed.input.name)
                if (feedSort === 'recent') return second.overview.latestTimestamp - first.overview.latestTimestamp
                return statusRank[first.overview.status] - statusRank[second.overview.status]
                    || second.overview.latestTimestamp - first.overview.latestTimestamp
            })
    }, [feedFilter, feedRows, feedSort, searchQuery])
    const pageSize = 20
    const pageCount = Math.max(1, Math.ceil(filteredFeedRows.length / pageSize))
    const visibleFeedRows = filteredFeedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    const allVisibleExpanded = visibleFeedRows.length > 0 && visibleFeedRows.every(({ feed }) => expandedFeedIds.has(feed.id))

    useEffect(() => {
        setCurrentPage(1)
    }, [feedFilter, feedSort, searchQuery])

    useEffect(() => {
        if (currentPage > pageCount) setCurrentPage(pageCount)
    }, [currentPage, pageCount])

    const toggleFeed = (feedId: string) => {
        setExpandedFeedIds((current) => {
            const next = new Set(current)
            if (next.has(feedId)) next.delete(feedId)
            else next.add(feedId)
            return next
        })
    }

    const toggleAllVisible = () => {
        setExpandedFeedIds((current) => {
            const next = new Set(current)
            visibleFeedRows.forEach(({ feed }) => {
                if (allVisibleExpanded) next.delete(feed.id)
                else next.add(feed.id)
            })
            return next
        })
    }

    return (
        <div className="container-fluid px-2 px-md-3 pb-4 feed-page">
            <div className="feed-page-header mb-4">
                <div className="feed-header-copy">
                    <h3 className="mb-1">Feed management</h3>
                    <p className="text-muted mb-0">
                        Build, transform and export product feeds from one workspace.
                    </p>
                </div>
                <Button onClick={() => handleOpenModal(getEmptyFeed())} className="feed-create-button">
                    <FaPlus aria-hidden="true" className="me-2" />
                    Create feed
                </Button>
            </div>

            {pageError && (feeds.length > 0 || isLoading) && (
                <Alert variant="danger" dismissible onClose={() => setPageError('')} role="alert">
                    <div className="d-flex align-items-start gap-2">
                        <FaExclamationTriangle className="mt-1 flex-shrink-0" aria-hidden="true" />
                        <span>{pageError}</span>
                    </div>
                </Alert>
            )}
            {notice && (
                <Alert variant="success" dismissible onClose={() => setNotice('')} role="status">
                    {notice}
                </Alert>
            )}

            {!isLoading && feeds.length > 0 && (
                <div className="feed-overview-bar mb-3" aria-label="Feed summary">
                    <div className="feed-overview-stat">
                        <FaRss className="feed-overview-icon text-primary" aria-hidden="true" />
                        <span><strong>{feeds.length}</strong> {feeds.length === 1 ? 'feed' : 'feeds'}</span>
                    </div>
                    <div className="feed-overview-stat">
                        <FaDatabase className="feed-overview-icon feed-overview-icon-info" aria-hidden="true" />
                        <span><strong>{sourceCount}</strong> {sourceCount === 1 ? 'data source' : 'data sources'}</span>
                    </div>
                    <div className={`feed-overview-stat ${attentionCount ? 'feed-overview-stat-warning' : ''}`}>
                        <span className={`feed-overview-icon ${
                            updatingCount
                                ? 'text-primary'
                                : attentionCount
                                    ? 'feed-overview-icon-warning'
                                    : 'text-success'
                        }`}>
                            {updatingCount
                                ? <FaArrowsRotate className="feed-spin" aria-hidden="true" />
                                : attentionCount
                                    ? <FaExclamationTriangle aria-hidden="true" />
                                    : <FaCheckCircle aria-hidden="true" />}
                        </span>
                        <span>
                            <strong>{updatingCount || attentionCount}</strong>{' '}
                            {updatingCount ? 'updating now' : 'issues found'}
                        </span>
                    </div>
                </div>
            )}

            {!isLoading && feeds.length > 0 && (
                <div className="feed-list-toolbar mb-3">
                    <div className="feed-search-wrap">
                        <FaMagnifyingGlass className="feed-search-icon" aria-hidden="true" />
                        <Form.Control
                            type="search"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            placeholder="Search feeds or sources"
                            aria-label="Search feeds or sources"
                        />
                    </div>
                    <Form.Select className="feed-toolbar-select" value={feedFilter} onChange={(event) => setFeedFilter(event.target.value as FeedFilter)} aria-label="Filter feeds">
                        <option value="all">All statuses</option>
                        <option value="attention">Needs attention</option>
                        <option value="updating">Updating</option>
                        <option value="ready">Successful</option>
                    </Form.Select>
                    <Form.Select className="feed-toolbar-select" value={feedSort} onChange={(event) => setFeedSort(event.target.value as FeedSort)} aria-label="Sort feeds">
                        <option value="attention">Problems first</option>
                        <option value="recent">Recently updated</option>
                        <option value="name">Name A–Z</option>
                    </Form.Select>
                    <Button type="button" variant="outline-secondary" size="sm" className="feed-expand-all" onClick={toggleAllVisible} disabled={visibleFeedRows.length === 0}>
                        {allVisibleExpanded ? 'Collapse all' : 'Expand all'}
                    </Button>
                    <span className="feed-result-count" aria-live="polite">
                        {filteredFeedRows.length} {filteredFeedRows.length === 1 ? 'feed' : 'feeds'}
                    </span>
                </div>
            )}

            {isLoading ? (
                <Card className="feed-state-card border-0 shadow-sm">
                    <Card.Body className="d-flex flex-column align-items-center justify-content-center py-5" role="status">
                        <Spinner animation="border" />
                        <div className="text-muted mt-3">Loading your feeds…</div>
                    </Card.Body>
                </Card>
            ) : pageError && feeds.length === 0 ? (
                <Card className="feed-state-card border-0 shadow-sm">
                    <Card.Body className="text-center py-5 px-3">
                        <div className="feed-empty-icon feed-empty-icon-danger mx-auto mb-3">
                            <FaExclamationTriangle aria-hidden="true" />
                        </div>
                        <h5 className="mb-2">Feeds could not be loaded</h5>
                        <p className="text-muted mx-auto mb-4 feed-empty-copy">
                            Check your connection and try loading the feed workspace again.
                        </p>
                        <Button variant="outline-primary" onClick={loadFeeds}>Try again</Button>
                    </Card.Body>
                </Card>
            ) : feeds.length === 0 ? (
                <Card className="feed-state-card border-0 shadow-sm">
                    <Card.Body className="text-center py-5 px-3">
                        <div className="feed-empty-icon mx-auto mb-3"><FaRss aria-hidden="true" /></div>
                        <h5 className="mb-2">No feeds yet</h5>
                        <p className="text-muted mx-auto mb-4 feed-empty-copy">
                            Create a feed to combine a URL or file source with export rules and an automatic schedule.
                        </p>
                        <Button onClick={() => handleOpenModal(getEmptyFeed())}>
                            <FaPlus aria-hidden="true" className="me-2" />
                            Create your first feed
                        </Button>
                    </Card.Body>
                </Card>
            ) : filteredFeedRows.length === 0 ? (
                <Card className="feed-state-card">
                    <Card.Body className="text-center py-5 px-3">
                        <div className="feed-empty-icon mx-auto mb-3"><FaMagnifyingGlass aria-hidden="true" /></div>
                        <h5 className="mb-2">No matching feeds</h5>
                        <p className="text-muted mx-auto mb-3 feed-empty-copy">Try another search or clear the current status filter.</p>
                        <Button variant="outline-primary" onClick={() => { setSearchQuery(''); setFeedFilter('all') }}>Clear filters</Button>
                    </Card.Body>
                </Card>
            ) : (
                <div className="feed-list">
                    <div className="feed-list-column-header" aria-hidden="true">
                        <span>Feed</span>
                        <span>Sources</span>
                        <span>Rules</span>
                        <span>Schedule</span>
                        <span>Status</span>
                        <span>Last run</span>
                        <span>Actions</span>
                    </div>
                    {visibleFeedRows.map(({ feed, overview }) => {
                        const isExpanded = expandedFeedIds.has(feed.id)
                        const isError = overview.status === 'error'
                        return (
                        <Card className="feed-card" key={feed.id}>
                            <Card.Header className="feed-card-header feed-master-row">
                                <button type="button" className="feed-master-toggle" onClick={() => toggleFeed(feed.id)} aria-expanded={isExpanded} aria-controls={`feed-details-${feed.id}`}>
                                    <span className="feed-master-cell feed-master-name">
                                        <span className="feed-master-chevron">
                                            {isExpanded ? <FaChevronDown aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
                                        </span>
                                        <span className="feed-card-title">{feed.input.name || 'Unnamed feed'}</span>
                                    </span>
                                    <span className="feed-master-cell feed-master-sources">
                                        <span className="feed-mobile-label">Sources</span>
                                        <span>{feed.input.links.length}</span>
                                    </span>
                                    <span className="feed-master-cell feed-master-rules">
                                        <span className="feed-mobile-label">Rules</span>
                                        <span>{feed.input.rules.length}</span>
                                    </span>
                                    <span className="feed-master-cell feed-master-schedule">
                                        <span className="feed-mobile-label">Schedule</span>
                                        <span>{formatUpdateInterval(feed.input.updateInterval)}</span>
                                        {formatGMT(feed.input.updateInterval) && <small>{formatGMT(feed.input.updateInterval)}</small>}
                                    </span>
                                    <span className="feed-master-cell feed-master-status-cell">
                                        <span className="feed-mobile-label">Status</span>
                                        <Badge
                                            bg={isError ? 'danger' : 'light'}
                                            text={isError ? undefined : 'dark'}
                                            className={`feed-master-status ${isError ? '' : `feed-master-status-${overview.status}`}`}
                                        >
                                            {overview.status === 'updating' && <Spinner animation="border" size="sm" aria-hidden="true" />}
                                            {overview.label}
                                        </Badge>
                                    </span>
                                    <span className="feed-master-cell feed-master-updated">
                                        <span className="feed-mobile-label">Last run</span>
                                        <span>{formatLastUpdated(overview.latestTimestamp ? new Date(overview.latestTimestamp).toISOString() : undefined)}</span>
                                    </span>
                                </button>
                                <div className="feed-master-side">
                                    <Button variant="outline-secondary" size="sm" className="feed-edit-button" onClick={() => handleOpenModal(feed)} aria-label={`Edit ${feed.input.name || 'feed'}`}>
                                        <FaEdit aria-hidden="true" />
                                    </Button>
                                </div>
                            </Card.Header>

                            {isExpanded && (feed.input.links.length === 0 ? (
                                <Card.Body className="text-muted text-center py-4">
                                    This feed has no data sources. Edit the feed to add one.
                                </Card.Body>
                            ) : (
                                <div
                                    className="feed-source-list"
                                    id={`feed-details-${feed.id}`}
                                    role="region"
                                    aria-label={`${feed.input.name || 'Feed'} sources`}
                                >
                                    <div className="feed-source-grid feed-source-grid-header" role="row" aria-hidden="true">
                                        <span>Source</span><span>Status</span><span>Products</span><span>Last run</span><span>Message</span><span>Actions</span>
                                    </div>
                                    {feed.input.links.map((link) => {
                                        const output = feed.output.find((item) => item.linkId === link.id)
                                        const runningKey = `${feed.id}_${link.id}`
                                        const isUpdating = runningFeeds[runningKey] || false
                                        const status = getStatus(output, isUpdating)
                                        const canDownload = output?.status === 1 && !isUpdating

                                        return (
                                            <div className="feed-source-grid feed-source-row" role="row" key={`${feed.id}-${link.id}`}>
                                                <div className="feed-source-cell feed-source-area" role="cell">
                                                    <span className="feed-source-type-icon">
                                                        {link.dataSourceType === 'file' ? <FaFile aria-hidden="true" /> : <FaLink aria-hidden="true" />}
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className="feed-source-name">{link.name || 'Unnamed source'}</span>
                                                        <small className="d-block text-muted mt-1 text-uppercase">
                                                            {link.dataSourceType === 'file' ? 'File' : 'URL'} · {link.kind} → {link.exportAs || 'csv'}
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="feed-status-area" role="cell">
                                                    <span className="feed-mobile-label">Status</span>
                                                    <Badge bg={status.variant} className="feed-status-badge">
                                                        {isUpdating && <Spinner animation="border" size="sm" aria-hidden="true" />}
                                                        {status.label}
                                                    </Badge>
                                                </div>
                                                <div className="feed-products-area text-nowrap" role="cell">
                                                    <span className="feed-mobile-label">Products</span>
                                                    {output?.productsBefore !== undefined ? (
                                                        <span>{formatNumberLocale(output.productsBefore, 0)}<span className="text-muted mx-2" aria-label="to">→</span><strong>{formatNumberLocale(output.productsAfter, 0)}</strong></span>
                                                    ) : <span className="text-muted">—</span>}
                                                </div>
                                                <div className="feed-updated-area text-nowrap" role="cell">
                                                    <span className="feed-mobile-label">Last run</span>
                                                    <span className={output?.lastUpdated ? '' : 'text-muted'}>{formatLastUpdated(output?.lastUpdated)}</span>
                                                </div>
                                                <div className="feed-message-area feed-message-cell" role="cell">
                                                    <span className="feed-mobile-label">Message</span>
                                                    <span title={output?.message || undefined}>{output?.message || '—'}</span>
                                                </div>
                                                <div className="feed-actions-area" role="cell">
                                                    <span className="feed-mobile-label">Actions</span>
                                                    <div className="feed-row-actions">
                                                        <Button variant="light" className="feed-icon-button" disabled={!canDownload} title={canDownload ? 'Download feed' : 'Run the source successfully before downloading'} aria-label={`Download ${link.name || 'source'}`} onClick={() => {
                                                            if (!canDownload) return
                                                            const origin = isDev ? 'http://localhost:3050' : 'https://api.affdash.nordpar.com'
                                                            handleDownloadClick(`${origin}/feed/${user?.username}/${feed.id}/${link.id}`, link.name || feed.input.name)
                                                        }}><span className="feed-icon-button-icon"><FaDownload aria-hidden="true" /></span></Button>
                                                        <Button variant="light" className="feed-icon-button" disabled={isUpdating} title={isUpdating ? 'Update in progress' : 'Update source now'} aria-label={`Update ${link.name || 'source'} now`} onClick={() => updateFeed(feed.id, link.id)}>
                                                            <span className="feed-icon-button-icon">
                                                                {isUpdating ? <FaArrowsRotate className="feed-spin" aria-hidden="true" /> : <FaArrowRotateRight aria-hidden="true" />}
                                                            </span>
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            ))}
                        </Card>
                    )})}

                    {pageCount > 1 && (
                        <nav className="feed-pagination" aria-label="Feed pages">
                            <Button variant="outline-secondary" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}>Previous</Button>
                            <span>Page {currentPage} of {pageCount}</span>
                            <Button variant="outline-secondary" size="sm" disabled={currentPage === pageCount} onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}>Next</Button>
                        </nav>
                    )}
                </div>
            )}

            <FeedInputModal
                show={showModal}
                onClose={handleCloseModal}
                onSave={handleSaveFeedInput}
                feedInput={currentFeed?.input}
                showRemove={Boolean(currentFeed?.id)}
                onRemove={() => {
                    if (!currentFeed) return
                    setFeedToRemove(currentFeed)
                    setRemoveError('')
                    setShowConfirmRemoveModal(true)
                }}
            />

            <ConfirmRemoveModal
                show={showConfirmRemoveModal}
                onHide={() => {
                    if (!isRemoving) {
                        setShowConfirmRemoveModal(false)
                        setRemoveError('')
                    }
                }}
                onConfirm={handleRemoveFeed}
                feedName={feedToRemove?.input.name || ''}
                isRemoving={isRemoving}
                error={removeError}
            />

            <DownloadModal
                show={downloadModalVisible}
                onHide={handleDownloadModalClose}
                downloadInfo={downloadInfo}
            />
        </div>
    )
}

export default Feed
